import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    interest: 'general'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit contact form');
      }

      const data = await response.json();
      toast.success('Thank you for reaching out! We will respond within 24-48 hours.');
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        interest: 'general'
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('An error occurred. Please try emailing us directly at info@theparkerfamilylegacyfoundation.org');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      content: 'Baton Rouge, Louisiana',
      subContent: 'United States'
    },
    {
      icon: Mail,
      title: 'Email Us',
      content: 'info@theparkerfamilylegacyfoundation.org',
      link: 'mailto:info@theparkerfamilylegacyfoundation.org'
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: '(225) 506-8722',
      link: 'tel:+12255068722'
    }
  ];

  return (
    <div className="min-h-screen bg-parchment">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/hkl9nftg_5EEA9B22-C9CC-4667-B8FA-D283940F398E.png"
            alt="Contact Us"
            className="w-full h-full object-cover opacity-15"
          />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-block mb-6">
            <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
            <h1 className="font-serif text-5xl md:text-7xl text-emerald-900 mb-6">Get In Touch</h1>
            <div className="h-px w-16 bg-gold mx-auto"></div>
          </div>
          <p className="text-xl md:text-2xl text-navy leading-relaxed">
            Let's partner together to build legacy and restore families
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="text-center bg-ivory p-8 rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-900 mb-6">
                  <info.icon className="h-8 w-8 text-gold" />
                </div>
                <h3 className="font-serif text-xl text-emerald-900 mb-3">{info.title}</h3>
                {info.link ? (
                  <a
                    href={info.link}
                    className="text-navy hover:text-gold transition-colors duration-300 block mb-1"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-navy mb-1">{info.content}</p>
                )}
                {info.subContent && <p className="text-navy/70 text-sm">{info.subContent}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 px-6 bg-ivory">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block mb-6">
              <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
              <h2 className="font-serif text-4xl text-emerald-900 mb-4">Send Us a Message</h2>
              <div className="h-px w-16 bg-gold mx-auto"></div>
            </div>
            <p className="text-lg text-navy">
              Whether you're interested in partnering, volunteering, or learning more about our work, 
              we'd love to hear from you.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-sm shadow-xl">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="name" className="text-emerald-900 font-semibold mb-2 block">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="border-emerald-900/20 focus:border-gold focus:ring-gold"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-emerald-900 font-semibold mb-2 block">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="border-emerald-900/20 focus:border-gold focus:ring-gold"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="phone" className="text-emerald-900 font-semibold mb-2 block">
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="border-emerald-900/20 focus:border-gold focus:ring-gold"
                  placeholder="(123) 456-7890"
                />
              </div>
              <div>
                <Label htmlFor="interest" className="text-emerald-900 font-semibold mb-2 block">
                  I'm interested in *
                </Label>
                <select
                  id="interest"
                  name="interest"
                  required
                  value={formData.interest}
                  onChange={handleChange}
                  className="w-full border border-emerald-900/20 rounded-md px-3 py-2 focus:border-gold focus:ring-gold focus:outline-none"
                >
                  <option value="general">General Inquiry</option>
                  <option value="donate">Making a Donation</option>
                  <option value="partner">Becoming a Partner</option>
                  <option value="volunteer">Volunteering</option>
                  <option value="services">Accessing Services</option>
                  <option value="board">Board/Leadership</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="subject" className="text-emerald-900 font-semibold mb-2 block">
                Subject *
              </Label>
              <Input
                id="subject"
                name="subject"
                type="text"
                required
                value={formData.subject}
                onChange={handleChange}
                className="border-emerald-900/20 focus:border-gold focus:ring-gold"
                placeholder="Brief subject of your message"
              />
            </div>

            <div className="mb-8">
              <Label htmlFor="message" className="text-emerald-900 font-semibold mb-2 block">
                Message *
              </Label>
              <Textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                rows={6}
                className="border-emerald-900/20 focus:border-gold focus:ring-gold resize-none"
                placeholder="Tell us more about your inquiry..."
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-emerald-900 hover:bg-emerald-800 text-white text-lg py-6 rounded-sm font-semibold transition-all duration-300 hover:scale-[1.02]"
            >
              {isSubmitting ? 'Sending...' : (
                <>
                  Send Message
                  <Send className="ml-3 h-5 w-5" />
                </>
              )}
            </Button>

            <p className="text-sm text-navy/60 text-center mt-6">
              We typically respond within 24-48 hours during business days.
            </p>
          </form>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6 bg-emerald-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Heart className="h-16 w-16 text-gold mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl mb-6">Ready to Make an Impact?</h2>
          <p className="text-xl text-ivory mb-8 leading-relaxed">
            Your support creates lasting change in the lives of mothers and families. 
            Every donation builds legacy, restores hope, and shelters futures.
          </p>
          <a href="/donate">
            <Button className="bg-gold hover:bg-gold-dark text-emerald-900 text-lg px-12 py-6 rounded-sm font-semibold transition-all duration-300 hover:scale-105">
              Make a Donation Today
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
